/**
 * Services de l'agenda.
 *
 * Lire est N1, écrire est N2 : un rendez-vous noté ne fait rien sortir de
 * l'entreprise. Ouvert aux trois rôles de bureau — le conducteur de travaux
 * pose ses réceptions de chantier, l'assistant prend les rendez-vous au
 * téléphone, et réserver l'agenda au patron garantirait surtout qu'il reste
 * vide.
 */
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { Appointment } from "./types.js";
import type {
  ListAppointmentsInput,
  RemoveAppointmentInput,
  SaveAppointmentInput,
} from "./schema.js";

export interface AppointmentRepository {
  list(ctx: ExecutionContext, from: string, to: string): Promise<Appointment[]>;
  save(
    ctx: ExecutionContext,
    command: Omit<SaveAppointmentInput, "confirmationId">,
  ): Promise<Appointment>;
  remove(ctx: ExecutionContext, id: string): Promise<void>;
}

const BUREAU = ["patron", "conducteur", "assistant"] as const;

export function createAgendaServices(repo: AppointmentRepository) {
  const listerRendezVous = defineService<ListAppointmentsInput, Appointment[]>({
    name: "lister_rendez_vous",
    level: ActionLevel.Read,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.list(ctx, input.from, input.to),
  });

  const enregistrerRendezVous = defineService<SaveAppointmentInput, Appointment>({
    name: "enregistrer_rendez_vous",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => {
      const { confirmationId: _ignored, ...command } = input;
      return repo.save(ctx, command);
    },
  });

  const supprimerRendezVous = defineService<RemoveAppointmentInput, void>({
    name: "supprimer_rendez_vous",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: [...BUREAU],
    handler: (ctx, input) => repo.remove(ctx, input.id),
  });

  return { listerRendezVous, enregistrerRendezVous, supprimerRendezVous };
}

export type AgendaServices = ReturnType<typeof createAgendaServices>;
